import { HttpClient } from '@/shared/infra/http/HttpClient';
import {
  AppliedI9Form,
  ExtensionDecision,
  ExtensionPreviewQuery,
  ExtensionRule,
  ExtensionRuleHistoryEntry,
  ExtensionRuleInput,
  I9Inspection,
  InspectorSearchResult,
  MatrixDocumentType,
  OutcomeI9Form,
  OutcomeMetric,
  Outcomes,
  MatrixStatus,
  MatrixStatusDetail,
  RuleChange,
  TpsDesignation,
  TpsDesignationInput,
} from '../../application/domain/entities/VisaMatrix';
import {
  DocumentCopySide,
  DocumentMoveAnswer,
  DocumentMoveDocument,
  DocumentMoveI9,
  DocumentMoveQueue,
  DocumentMoveQueueQuery,
  DocumentMoveRun,
  StartDocumentMoveRun,
} from '../../application/domain/entities/DocumentMove';

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.append(k, v);
  return q.toString();
};

/** The Visa Matrix screens' reads and writes (VTK-2063). Super admins only on the server. */
export class ApiVisaMatrixAdminRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async listStatuses(on: string): Promise<MatrixStatus[]> {
    const res = await this.httpClient.get<{ statuses: MatrixStatus[] }>(
      `/visa-matrix/statuses?${query({ on })}`
    );
    return res.data?.statuses ?? [];
  }

  async getStatus(code: string, on: string): Promise<MatrixStatusDetail> {
    const res = await this.httpClient.get<MatrixStatusDetail>(
      `/visa-matrix/statuses/${encodeURIComponent(code)}?${query({ on })}`
    );
    return res.data;
  }

  async listDocumentTypes(on: string): Promise<MatrixDocumentType[]> {
    const res = await this.httpClient.get<{ documentTypes: MatrixDocumentType[] }>(
      `/visa-matrix/document-types?${query({ on })}`
    );
    return res.data?.documentTypes ?? [];
  }

  async changes(from: string, to: string): Promise<RuleChange[]> {
    const res = await this.httpClient.get<{ changes: RuleChange[] }>(
      `/visa-matrix/changes?${query({ from, to })}`
    );
    return res.data?.changes ?? [];
  }

  async listExtensionRules(): Promise<ExtensionRule[]> {
    const res = await this.httpClient.get<ExtensionRule[]>('/ead-extension-rules');
    return res.data ?? [];
  }

  async preview(q: ExtensionPreviewQuery): Promise<ExtensionDecision | null> {
    const res = await this.httpClient.get<{ decision: ExtensionDecision | null }>(
      `/ead-extension-rules/resolve?${query(q as unknown as Record<string, string>)}`
    );
    return res.data?.decision ?? null;
  }

  async history(code: string): Promise<ExtensionRuleHistoryEntry[]> {
    const res = await this.httpClient.get<ExtensionRuleHistoryEntry[]>(
      `/ead-extension-rules/${encodeURIComponent(code)}/history`
    );
    return res.data ?? [];
  }

  async appliedI9Forms(code: string): Promise<AppliedI9Form[]> {
    const res = await this.httpClient.get<AppliedI9Form[]>(
      `/ead-extension-rules/${encodeURIComponent(code)}/i9-forms`
    );
    return res.data ?? [];
  }

  async add(input: ExtensionRuleInput): Promise<ExtensionRule> {
    const res = await this.httpClient.post<ExtensionRule>('/ead-extension-rules', input);
    return res.data;
  }

  async replace(code: string, input: ExtensionRuleInput): Promise<ExtensionRule> {
    const res = await this.httpClient.post<ExtensionRule>(
      `/ead-extension-rules/${encodeURIComponent(code)}/versions`,
      input
    );
    return res.data;
  }

  async end(code: string, endAt: string, reason: string): Promise<ExtensionRule> {
    const res = await this.httpClient.patch<ExtensionRule>(
      `/ead-extension-rules/${encodeURIComponent(code)}/end`,
      { endAt, reason }
    );
    return res.data;
  }

  async listTpsDesignations(): Promise<TpsDesignation[]> {
    const res = await this.httpClient.get<TpsDesignation[]>(
      '/ead-extension-rules/tps-designations'
    );
    return res.data ?? [];
  }

  async addTpsDesignation(input: TpsDesignationInput): Promise<TpsDesignation> {
    const res = await this.httpClient.post<TpsDesignation>(
      '/ead-extension-rules/tps-designations',
      input
    );
    return res.data;
  }

  async activateTpsDesignation(id: string, reason: string): Promise<TpsDesignation> {
    const res = await this.httpClient.patch<TpsDesignation>(
      `/ead-extension-rules/tps-designations/${encodeURIComponent(id)}/activate`,
      { reason }
    );
    return res.data;
  }

  async searchI9Forms(q: string): Promise<InspectorSearchResult[]> {
    const res = await this.httpClient.get<InspectorSearchResult[]>(
      `/visa-matrix/i9-forms?${query({ q })}`
    );
    return res.data ?? [];
  }

  async inspect(i9FormId: string): Promise<I9Inspection> {
    const res = await this.httpClient.get<I9Inspection>(
      `/visa-matrix/i9-forms/${encodeURIComponent(i9FormId)}/inspection`
    );
    return res.data;
  }

  async outcomes(from?: string, to?: string): Promise<Outcomes> {
    const res = await this.httpClient.get<Outcomes>(`/visa-matrix/outcomes?${query({ from, to })}`);
    return res.data;
  }

  /** The I-9s behind one count, read within the same dates the count was. */
  async outcomeI9Forms(
    metric: OutcomeMetric,
    weekStart: string,
    key: string | null,
    range: { from: string; to: string }
  ): Promise<OutcomeI9Form[]> {
    const res = await this.httpClient.get<OutcomeI9Form[]>(
      `/visa-matrix/outcomes/${metric}/i9-forms?${query({ weekStart, key: key ?? undefined, ...range })}`
    );
    return res.data ?? [];
  }

  /** The staff queue of I-9s the move into the new document format found something on (VTK-2089). */
  async documentMoveQueue(q: DocumentMoveQueueQuery): Promise<DocumentMoveQueue> {
    const res = await this.httpClient.get<DocumentMoveQueue>(
      `/document-migration/queue?${query({
        status: q.status,
        kind: q.kind,
        reason: q.reason,
        hasSuggestions: q.hasSuggestions ? 'true' : undefined,
        page: q.page ? String(q.page) : undefined,
        pageSize: q.pageSize ? String(q.pageSize) : undefined,
      })}`
    );
    return res.data;
  }

  async documentMoveI9(i9FormId: string): Promise<DocumentMoveI9> {
    const res = await this.httpClient.get<DocumentMoveI9>(
      `/document-migration/queue/${encodeURIComponent(i9FormId)}`
    );
    return res.data;
  }

  /** The latest runs of the move, the scan reading and the drift check, newest first. */
  async documentMoveRuns(): Promise<DocumentMoveRun[]> {
    const res = await this.httpClient.get<DocumentMoveRun[]>('/document-migration/runs');
    return res.data ?? [];
  }

  /** Starts a run; the server returns at once and the run goes on in the background. */
  async startDocumentMoveRun(request: StartDocumentMoveRun): Promise<DocumentMoveRun> {
    const res = await this.httpClient.post<DocumentMoveRun>('/document-migration/runs', request);
    return res.data;
  }

  async cancelDocumentMoveRun(runId: string): Promise<DocumentMoveRun> {
    const res = await this.httpClient.post<DocumentMoveRun>(
      `/document-migration/runs/${encodeURIComponent(runId)}/cancel`,
      {}
    );
    return res.data;
  }

  /** One stored copy of a document, a picture or a PDF. The server logs that this person was shown it. */
  async documentMoveCopy(
    i9FormId: string,
    document: Pick<DocumentMoveDocument, 'source' | 'supplementBId' | 'list'>,
    side: DocumentCopySide
  ): Promise<Blob> {
    const res = await this.httpClient.get(
      `/document-migration/queue/${encodeURIComponent(i9FormId)}/documents/${document.source}/${document.list}/${side}/image?${query({ supplementBId: document.supplementBId ?? undefined })}`,
      { responseType: 'blob' }
    );
    return res as unknown as Blob;
  }

  /** Answers blank fields from the original document; the I-9 moves when nothing is left. */
  async resolveDocumentMove(
    i9FormId: string,
    answers: DocumentMoveAnswer[]
  ): Promise<DocumentMoveI9> {
    const res = await this.httpClient.post<DocumentMoveI9>(
      `/document-migration/queue/${encodeURIComponent(i9FormId)}/resolve`,
      { answers }
    );
    return res.data;
  }

  /** A second person's decision on a problem raised on the original; lifts its hold. */
  async closeDocumentMoveDefect(itemId: string, note: string): Promise<DocumentMoveI9> {
    const res = await this.httpClient.post<DocumentMoveI9>(
      `/document-migration/queue/items/${encodeURIComponent(itemId)}/close`,
      { note }
    );
    return res.data;
  }

  async handleDocumentMoveItem(itemId: string, note: string): Promise<DocumentMoveI9> {
    const res = await this.httpClient.post<DocumentMoveI9>(
      `/document-migration/queue/items/${encodeURIComponent(itemId)}/handled`,
      { note }
    );
    return res.data;
  }
}
